import { Injectable } from '@angular/core';

const STORAGE_KEY = 'medone.audit-session-id';

/**
 * Holds the session id used for the audit `X-Session-Id` header, which the
 * interceptor attaches to every admin-API request (reads included, not only
 * writes): a client-generated GUID persisted to `sessionStorage` so it
 * survives page refreshes within a tab. It is rotated on each successful login
 * (see `AuthService.initialize`) and cleared on logout, so one id maps to one
 * login session in one tab.
 *
 * The id is an opaque correlation value — not PHI and not a credential — so
 * persisting it is guardrail-safe (same category as `ThemeService`'s stored
 * theme preference). If storage is unavailable the service degrades to an
 * in-memory id for the page lifetime; `sessionId` is always populated.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionContextService {
  private currentSessionId: string | null = null;

  /**
   * Set by {@link clear} and lifted by {@link rotate}. Logout is not instantaneous — the Okta
   * redirect leaves the app running, and any in-flight or retried admin-API call still reads
   * `sessionId`. Without this latch that read would re-mint *and re-persist*, resurrecting the
   * storage key `clear()` just removed and carrying an id across the logout boundary.
   */
  private isCleared = false;

  get sessionId(): string {
    if (!this.currentSessionId) {
      // Post-logout reads stay in memory: still always populated (the interceptor needs a
      // value), but nothing is read from or written back to storage until the next login.
      this.currentSessionId = this.isCleared
        ? crypto.randomUUID()
        : (this.readFromStorage() ?? this.mint());
    }
    return this.currentSessionId;
  }

  /** Mint a fresh id on a successful login so 'session' aligns with 'login session'. */
  rotate(): void {
    this.isCleared = false;
    this.currentSessionId = this.mint();
  }

  /** Drop the session id on logout so the next login starts fresh. */
  clear(): void {
    this.isCleared = true;
    this.currentSessionId = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage unavailable — nothing persisted to remove.
    }
  }

  private mint(): string {
    const id = crypto.randomUUID();
    try {
      sessionStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Storage unavailable (e.g. blocked in private mode) — keep in memory only.
    }
    return id;
  }

  private readFromStorage(): string | null {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }
}
